import React, { useState, useEffect, useMemo } from "react";
import { Modal } from "react-bootstrap";
import {
    useReactTable,
    getCoreRowModel,
    flexRender,
    ColumnDef,
} from '@tanstack/react-table';
import { viewChatbotsByClient, ChatBot } from "../../services/dashboard/ViewChatbotsByClient";
import LoadingSpinner from "../utility/LoadingSpinner";
import "../../styles/dash/ViewChatBotsByClientModal.css";
import "../../styles/dash/BotsSection.css"; // Reusing table styles


interface Props {
    show: boolean;
    onClose: () => void;
    clientId: number | null;
    clientName: string;
}

const ViewChatBotsByClientModal: React.FC<Props> = ({ show, onClose, clientId, clientName }) => {
    const [chatbots, setChatbots] = useState<ChatBot[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [animateOut, setAnimateOut] = useState(false);

    useEffect(() => {
        if (show && clientId) {
            const fetchChatbots = async () => {
                setLoading(true);
                setError(null);
                setChatbots([]);
                try {
                    const response = await viewChatbotsByClient(clientId);
                    if (response.status === 200) {
                        setChatbots(response.data);
                        if (response.data.length === 0) {
                            setError("No se encontraron chatbots para este cliente.");
                        }
                    } else {
                        setError(response.message || "No se pudieron cargar los chatbots.");
                    }
                } catch (err: any) {
                    setError(err.message || 'Error al cargar los chatbots.');
                } finally {
                    setLoading(false);
                }
            };
            fetchChatbots();
        }
    }, [show, clientId]);

    const handleClose = () => {
        setAnimateOut(true);
        setTimeout(() => {
            onClose();
            setAnimateOut(false);
            setError(null);
        }, 300);
    };

    const columns = useMemo<ColumnDef<ChatBot>[]>(() => [
        {
            accessorKey: 'nombre',
            header: 'Nombre',
            cell: info => info.getValue(),
        },
        {
            accessorKey: 'key',
            header: 'Key',
            cell: info => info.getValue(),
        },
        {
            accessorKey: 'color',
            header: 'Color',
            cell: info => (
                <span className="color-badge" style={{ backgroundColor: (info.getValue() as string) || '#ccc' }}>
                    {info.getValue() as string}
                </span>
            ),
        },
        {
            accessorKey: 'status',
            header: 'Estado',
            cell: info => {
                const status = info.getValue<number>();
                const statusText = status === 1 ? 'Activo' : 'Inactivo';
                return (
                    <span className={`status-text ${statusText.toLowerCase()}`}>
                        {statusText}
                    </span>
                );
            },
        },
    ], []);

    const table = useReactTable({
        data: chatbots,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    if (!show && !animateOut) return null;

    return (
        <Modal
            show={!animateOut}
            onHide={handleClose}
            centered
            size="xl"
            backdrop="static"
            dialogClassName="view-chatbots-dialog"
            contentClassName={`view-chatbots-modal ${animateOut ? "modal-exit" : "modal-enter"}`}
        >
            <Modal.Header closeButton closeVariant="white" className="border-0" />
            <Modal.Body className="view-chatbots-content">
                <h5 className="view-chatbots-title text-center mb-4">
                    Chatbots del Cliente: {clientName}
                </h5>

                {loading ? (
                    <LoadingSpinner />
                ) : error && chatbots.length === 0 ? (
                    <div className="view-chatbots-no-data-message">{error}</div>
                ) : (
                    <div className="chatbots-table-container">
                        <table className="chatbots-table">
                            <colgroup>
                                <col style={{ width: '25%' }} /> {/* Nombre */}
                                <col style={{ width: '35%' }} /> {/* Key */}
                                <col style={{ width: '20%' }} /> {/* Color */}
                                <col style={{ width: '20%' }} /> {/* Estado */}
                            </colgroup>
                            <thead>
                                {table.getHeaderGroups().map(headerGroup => (
                                    <tr key={headerGroup.id}>
                                        {headerGroup.headers.map(header => (
                                            <th key={header.id} className={
                                                header.column.id === 'nombre' ? 'nombre-column' :
                                                    header.column.id === 'color' ? 'color-column' :
                                                        header.column.id === 'status' ? 'state-column' : ''
                                            }>
                                                {flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>
                            <tbody>
                                {table.getRowModel().rows.map(row => (
                                    <tr key={row.id}>
                                        {row.getVisibleCells().map(cell => (
                                            <td key={cell.id} className={
                                                cell.column.id === 'nombre' ? 'nombre-column' :
                                                    cell.column.id === 'key' ? '' :
                                                        cell.column.id === 'color' ? 'color-column' :
                                                            cell.column.id === 'status' ? 'state-column' : ''
                                            }>
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext()
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="view-chatbots-footer">
                    <button type="button" className="view-chatbots-close-button" onClick={handleClose}>
                        Cerrar
                    </button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default ViewChatBotsByClientModal;